import React, { useState } from "react";
import {
  Button,
  Modal,
  Alert,
  Space,
  notification
} from "antd";
import {
  SendOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import WorkflowDiagram from "../../purchase-template/components/WorkflowDiagram";
import { useSubmitProcurementPlanReport } from "../hooks/useSubmitProcurementPlanReport";

// Component Props Interface
export interface SubmitButtonProps {
  reportId?: string;
  report?: any;
  buttonText?: string;
  buttonType?: "default" | "primary";
  icon?: React.ReactNode;
  onSuccess?: () => void;
  disabled?: boolean;
  size?: "small" | "middle" | "large";
}

export const SubmitButton: React.FC<SubmitButtonProps> = ({
  reportId,
  report,
  buttonText = "Submit",
  buttonType = "primary",
  icon = <SendOutlined />,
  onSuccess,
  disabled = false,
  size = "middle"
}) => {
  const [isConfirmationModalVisible, setIsConfirmationModalVisible] = useState(false);

  // Submit mutation hook using custom hook
  const { submit: submitProcurementPlanReport, isLoading } = useSubmitProcurementPlanReport({
    onSuccess: () => {
      // Call the parent's onSuccess callback if provided
      onSuccess?.();
    },
    onError: (error) => {
      console.error('Submit error:', error);
    }
  });

  // Handle form submission - show confirmation modal
  const handleSubmit = () => {
    if (!report && !reportId) {
      console.error('Either reportId or report must be provided');
      notification.error({
        message: 'Submission Error',
        description: 'Invalid submission parameters. Please try again.',
      });
      return;
    }

    // Show confirmation modal
    setIsConfirmationModalVisible(true);
  };

  // Handle confirmation modal OK
  const handleConfirmationOk = async () => {
    setIsConfirmationModalVisible(false);
    try {
      // Proceed with actual submission
      if (reportId) {
        await submitProcurementPlanReport({
          reportId: reportId,
        });
      } else if (report) {
        await submitProcurementPlanReport({
          reportId: report.id,
        });
      }
    } catch (error) {
      console.error('Submission failed:', error);
    }
  };

  // Handle confirmation modal cancel
  const handleConfirmationCancel = () => {
    setIsConfirmationModalVisible(false);
  };

  return (
    <>
      {/* Submit Button */}
      <Button
        type={buttonType}
        onClick={handleSubmit}
        icon={icon}
        loading={isLoading}
        disabled={disabled}
        size={size}
      >
        {buttonText}
      </Button>

      {/* Confirmation Modal */}
      <Modal
        title={
          <Space>
            <CheckCircleOutlined style={{ color: '#52c41a' }} />
            Confirm Submission
          </Space>
        }
        open={isConfirmationModalVisible}
        onOk={handleConfirmationOk}
        onCancel={handleConfirmationCancel}
        okText="Submit"
        cancelText="Cancel"
        width={800}
        destroyOnClose
      >
        <div style={{ marginBottom: 16 }}>
          <Alert
            message="Ready to Submit"
            description="Your procurement plan report is ready to be submitted for approval."
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
          />
        </div>

        {/* Flow Diagram */}
        {(() => {
          const externalWorkflow = report?.externalWorkflow;
          const approvalType = report?.approvalType;
          
          return approvalType === 'EXTERNAL' && externalWorkflow ? (
            <div style={{ marginBottom: 16 }}>
              <h4 style={{ marginBottom: 12, fontWeight: 600 }}>Approval Flow:</h4>
              <WorkflowDiagram flow={externalWorkflow} />
            </div>
          ) : null;
        })()}

        {report?.approvalType === 'LOCAL' && (
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ marginBottom: 12, fontWeight: 600 }}>Approval Flow:</h4>
            <Alert
              message="Local Approval Process"
              description="This report will follow the standard local approval workflow."
              type="info"
              showIcon
            />
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <p style={{ marginBottom: 8, fontWeight: 500 }}>
            By clicking "Submit", you confirm that:
          </p>
          <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
            <li style={{ marginBottom: 4 }}>
              The first approver will be notified immediately
            </li>
            <li style={{ marginBottom: 4 }}>
              The procurement plan report will be marked as submitted
            </li>
            <li style={{ marginBottom: 0 }}>
              You will no longer be able to edit this report
            </li>
          </ul>
        </div>
      </Modal>
    </>
  );
};

export default SubmitButton;